import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import logo from '@assets/img/logo-white.png';
import CenterCenterStack from '@components/CenterCenterStack';
import If from '@components/If';
import { OrganizationInfo } from '@core/hooks/useOrganization';
import useUserUid from '@core/hooks/useUserUid';
import { useLoading } from '@core/providers/LoadingAppProvider';
import useUserOrganizations from '../../hooks/useUserOrganizations';
import Loading from './Loading';
import OrganizationList from './OrganizationList';
import useSx from './sx';

export interface OrganizationPickerProps {
  onOrganizationPicked: (organization: OrganizationInfo) => void;
}

const OrganizationSetter = ({ onOrganizationPicked }: OrganizationPickerProps) => {
  const { setLoadingApp } = useLoading();
  const userUid = useUserUid();
  const { organizations, isLoading } = useUserOrganizations(userUid);
  const [searchTerm, setSearchTerm] = useState('');
  const sx = useSx();

  const handleOrgListClick = (organizationId: string) => {
    onOrganizationPicked({ organizationId, hasMoreOrganizations: true });
  };

  const filteredOrganizations = useMemo(() => {
  if (!organizations) return [];

  const matches = organizations.filter((org) =>
    org.organizationName.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (searchTerm.trim() === '') {
    return matches.slice(0, 10);
  }

  return matches;
}, [organizations, searchTerm]);

  useEffect(() => {
    if (!isLoading && organizations.length === 1) {
      const [{ organizationProductId }] = organizations;
      onOrganizationPicked({ organizationId: organizationProductId, hasMoreOrganizations: false });
    }
  }, [isLoading, onOrganizationPicked, organizations]);

  useEffect(() => {
    if (!isLoading && organizations.length > 1) setLoadingApp(false);
  }, [organizations, isLoading, setLoadingApp]);
    
  return (
    <If condition={!isLoading && organizations.length > 1}>
      <CenterCenterStack sx={sx.centerCenterSx}>
        <Paper sx={sx.root}>
          <Box component="img" src={logo} />
          <Typography variant="h5" component="p">
            Seleccione una organizaci&oacute;n
          </Typography>
          <Typography component="p">para continuar a Evodicom&reg;.</Typography>
          {organizations.length >= 5 && (
            <Box sx={sx.searchInputContainer}>
              <TextField
                type="text"
                placeholder="Buscar organización..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={sx.searchInput}
              />
            </Box>
          )}
          <If condition={!isLoading} fallback={<Loading />}>
            <OrganizationList organizations={filteredOrganizations} onClick={handleOrgListClick} />
           </If>
        </Paper>
      </CenterCenterStack>
    </If>
  );
};

export default OrganizationSetter;
